import React, { useEffect, useRef, useState } from "react";
import {
  Form,
  Input,
  Label,
  FormFeedback,
  Button,
  Row,
  Col,
  Table,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
  Spinner,
  Card,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import PurchaseService from "../../../services/OrderingServices/PurchaseService";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import ItemService from "../../../services/OrderingServices/ItemService";
import SupplierService from "../../../services/OrderingServices/SupplierService";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";

// Validation Schema
const validationSchema = Yup.object({
  purchaseDate: Yup.date().required("Purchase date and time is required"),
  supplierId: Yup.number().required("Supplier is required"),
  items: Yup.array()
    .of(
      Yup.object({
        productId: Yup.number().required("Product is required"),
        productName: Yup.string().required("Product name is required"),
        quantity: Yup.number()
          .min(1, "Quantity must be at least 1")
          .required("Required"),
        unitPrice: Yup.number()
          .min(0, "Price cannot be negative")
          .required("Required"),
      })
    )
    .min(1, "At least one item is required"),
  voucherNo: Yup.string().required("Voucher number is required"),
});

const PurchaseForm = ({ modal, toggleModal, fetchPurchase, selectedItem }) => {
  // const { id } = useParams();
  const [orgDetail, setOrgDetail] = useState(null);
  const [productData, setProductData] = useState([]);
  const [supplierList, setSupplierList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openAccordion, setOpenAccordion] = useState("basicInfo"); // Accordion state
  const navigate = useNavigate();
  const flatpickrRef = useRef(null);

  const toggleAccordion = (id) => {
    setOpenAccordion((prev) => (prev === id ? null : id));
  };

  // Fetch purchase details
  useEffect(() => {
    const fetchOrgDetail = async () => {
      if (selectedItem) {
        try {
          const response = await PurchaseService.detail(selectedItem);
          setOrgDetail(response);
        } catch (error) {
          console.error("Error fetching purchase details:", error);
          toast.error("Failed to load purchase details");
        }
      }
    };
    fetchOrgDetail();
  }, [selectedItem]);

  // Fetch products
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await ItemService.itemList();
        setProductData(response);
      } catch (error) {
        console.error("Error fetching products:", error);
        toast.error("Failed to load products");
      }
    };
    fetchProduct();
  }, []);

  // Fetch suppliers
  useEffect(() => {
    const fetchSupplier = async () => {
      try {
        setLoading(true);
        const response = await SupplierService.getList(1, 100);
        setSupplierList(response);
      } catch (error) {
        console.error("Error fetching suppliers:", error);
        toast.error("Failed to load suppliers");
      } finally {
        setLoading(false);
      }
    };
    fetchSupplier();
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      purchaseDate: orgDetail?.purchaseDate || "",
      supplierId: orgDetail?.supplierId || "",
      voucherNo: orgDetail?.voucherNo || orgDetail?.refrenceNo || "",
      items: orgDetail?.items || [
        {
          productId: "",
          productName: "",
          quantity: "",
          unitPrice: "",
        },
      ],
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const payload = {
          id: parseInt(selectedItem),
          purchaseDate: new Date(values.purchaseDate).toISOString(),
          supplierId: parseInt(values.supplierId),
          voucherNo: values.voucherNo,
          items: values.items.map((item) => ({
            productId: parseInt(item.productId),
            productName: item.productName,
            quantity: parseInt(item.quantity),
            unitPrice: parseFloat(item.unitPrice),
          })),
        };

        if (selectedItem) {
          await PurchaseService.update(selectedItem, payload);
          toast.success("Purchase updated successfully");
        } else {
          await PurchaseService.create(payload);
          toast.success("Purchase created successfully");
          resetForm();
        }
        fetchPurchase();
        toggleModal();
      } catch (error) {
        console.error("Error submitting form:", error);
        toast.error(
          error?.response?.data?.message || "Failed to save purchase"
        );
      }
    },
  });

  const productOptions =
    productData?.items?.map((type) => ({
      value: type.id,
      label: type.name,
      price: type.price,
    })) || [];

  const supplierOptions =
    supplierList?.map((supplier) => ({
      value: supplier.id,
      label: supplier.name,
    })) || [];

  const handleAddItem = () => {
    formik.setFieldValue("items", [
      ...formik.values.items,
      {
        productId: "",
        productName: "",
        quantity: "",
        unitPrice: "",
      },
    ]);
  };

  const handleRemoveItem = (index) => {
    const newItems = formik.values.items.filter((_, i) => i !== index);
    formik.setFieldValue("items", newItems);
  };

  const handleProductChange = (index, selectedOption) => {
    formik.setFieldValue(
      `items[${index}].productId`,
      selectedOption ? selectedOption.value : ""
    );
    formik.setFieldValue(
      `items[${index}].productName`,
      selectedOption ? selectedOption.label : ""
    );
    formik.setFieldValue(
      `items[${index}].unitPrice`,
      selectedOption ? selectedOption.price || "" : ""
    );
  };

  return (
    <Offcanvas
      isOpen={modal}
      toggle={toggleModal}
      direction="end"
      scrollable
      style={{ width: "600px" }}
    >
      <OffcanvasHeader toggle={toggleModal} className="border-bottom">
        {selectedItem ? "Update Purchase" : "Add Purchase"}
      </OffcanvasHeader>
      <Form
        onSubmit={formik.handleSubmit}
        className="d-flex flex-column"
        style={{ height: "calc(100vh - 73px)" }}
      >
        <div className="flex-grow-1 overflow-auto p-3">
          <Accordion open={openAccordion} toggle={toggleAccordion}>
            {/* Basic Info */}
            <AccordionItem>
              <AccordionHeader
                targetId="basicInfo"
                toggle={() => toggleAccordion("basicInfo")}
              >
                Basic Information
              </AccordionHeader>
              <AccordionBody accordionId="basicInfo">
                <Row>
                  <Col lg={6}>
                    <Label>Purchase Date & Time</Label>
                    <Flatpickr
                      placeholder="Select Date & Time"
                      id="purchaseDate"
                      name="purchaseDate"
                      className={`form-control ${
                        formik.touched.purchaseDate &&
                        formik.errors.purchaseDate
                          ? "is-invalid"
                          : ""
                      }`}
                      options={{
                        enableTime: true,
                        dateFormat: "Y-m-d H:i",
                        time_24hr: true,
                      }}
                      value={formik.values.purchaseDate}
                      onChange={([date]) =>
                        formik.setFieldValue("purchaseDate", date)
                      }
                      onBlur={() =>
                        formik.setFieldTouched("purchaseDate", true)
                      }
                    />
                    {formik.touched.purchaseDate &&
                      formik.errors.purchaseDate && (
                        <div className="text-danger mt-1">
                          {formik.errors.purchaseDate}
                        </div>
                      )}
                  </Col>
                  <Col lg={6}>
                    <Label>Supplier</Label>
                    <Select
                      options={supplierOptions}
                      value={
                        supplierOptions.find(
                          (option) => option.value === formik.values.supplierId
                        ) || null
                      }
                      onChange={(option) =>
                        formik.setFieldValue(
                          "supplierId",
                          option ? option.value : ""
                        )
                      }
                      placeholder="Select Supplier"
                      isClearable
                    />

                    {formik.touched.supplierId && formik.errors.supplierId && (
                      <div className="text-danger mt-1">
                        {formik.errors.supplierId}
                      </div>
                    )}
                  </Col>
                  <Col lg={6}>
                    <Label>Voucher No.</Label>
                    <Input
                      type="text"
                      name="voucherNo"
                      placeholder="Enter Voucher No."
                      value={formik.values.voucherNo}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={
                        !!(
                          formik.touched.voucherNo && formik.errors.voucherNo
                        )
                      }
                    />
                    {formik.touched.voucherNo && formik.errors.voucherNo && (
                      <div className="text-danger mt-1">
                        {formik.errors.voucherNo}
                      </div>
                    )}
                  </Col>
                </Row>
              </AccordionBody>
            </AccordionItem>

            {/* Items */}
            <AccordionItem>
              <AccordionHeader
                targetId="itemsInfo"
                toggle={() => toggleAccordion("itemsInfo")}
              >
                Items ({formik.values.items.length})
              </AccordionHeader>
              <AccordionBody accordionId="itemsInfo">
                {formik.values.items.map((item, index) => (
                  <Card key={index} className="mb-2 p-2 position-relative">
                    <Row className="align-items-center g-2">
                      <Col
                        xs={12}
                        className="d-flex justify-content-between align-items-start"
                      >
                        <h6>Item {index + 1}</h6>
                        <Button
                          color="danger"
                          size="sm"
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          className="p-1"
                          style={{ height: "30px", width: "30px" }}
                        >
                          <i className="bx bx-trash"></i>
                        </Button>
                      </Col>
                      <Col xs={12} md={12} className="mb-2">
                        <Label>Product</Label>
                        <Select
                          options={productOptions}
                          value={
                            productOptions.find(
                              (option) => option.value === item.productId
                            ) || null
                          }
                          onChange={(option) =>
                            handleProductChange(index, option)
                          }
                          placeholder="Select Product"
                          isClearable
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          }}
                        />
                      </Col>
                      <Col xs={6} md={6} className="mb-2">
                        <Label>Quantity</Label>
                        <Input
                          name={`items[${index}].quantity`}
                          type="number"
                          value={item.quantity}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder="Qty"
                          invalid={
                            !!(
                              formik.touched.items?.[index]?.quantity &&
                              formik.errors.items?.[index]?.quantity
                            )
                          }
                        />
                        <FormFeedback>
                          {formik.errors.items?.[index]?.quantity}
                        </FormFeedback>
                      </Col>
                      <Col xs={6} md={6} className="mb-2">
                        <Label>Unit Price</Label>
                        <Input
                          name={`items[${index}].unitPrice`}
                          type="number"
                          value={item.unitPrice}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder="Price"
                          invalid={
                            !!(
                              formik.touched.items?.[index]?.unitPrice &&
                              formik.errors.items?.[index]?.unitPrice
                            )
                          }
                        />
                        <FormFeedback>
                          {formik.errors.items?.[index]?.unitPrice}
                        </FormFeedback>
                      </Col>
                    </Row>
                  </Card>
                ))}

                <div className="d-flex justify-content-center mt-2">
                  <Button
                    color="success"
                    size="sm"
                    type="button"
                    onClick={handleAddItem}
                  >
                    <i className="bx bx-plus"></i> Add Item
                  </Button>
                </div>
              </AccordionBody>
            </AccordionItem>
          </Accordion>
        </div>

        <div className="bg-white border-top p-3 d-flex justify-content-end gap-2">
          <Button color="danger" onClick={toggleModal}>
            Cancel
          </Button>
          <Button color="success" type="submit" disabled={loading}>
            {selectedItem ? "Update" : "Create"}
            {loading && <Spinner size="sm" className="ms-2" />}
          </Button>
        </div>
      </Form>
    </Offcanvas>
  );
};

export default PurchaseForm;
